import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontWeight, LiliButton, LiliSwitch, Text } from '@lili/lili-frontend-common';
import { Skeleton } from 'primereact/skeleton';
import { useStore } from '../../../../store';
import { useAccountSummaryQuery } from '../../../../hooks/query/useAccountSummaryQuery';
import { AUTO_SAVING_TEXTS } from '../../constants';
import Donut from '../../../../generic-components/Donut';
import { useTaxBucketSetPercentAndActivate } from '../../../../hooks/query/useTaxBucketSetPercentAndActivate';

const DISABLED_SAVE_PERCENT_VALUE = 0.0;

const DEFAULT_SAVE_PERCENT_VALUE = 0.2;

const TaxBucketAutoSaving = () => {
  const { MAIN_TEXT, BUTTON_TEXT, DISCLAIMER, HEADER } = AUTO_SAVING_TEXTS;
  const navigate = useNavigate();
  const summary = useStore((state) => state.summary);
  const [autoSavingOn, setAutoSavingOn] = useState<boolean>(!!summary?.taxBucketBalance?.savePercent);
  const [savePercent, setSavePercent] = useState<number>(summary?.taxBucketBalance?.savePercent);
  const { customerId } = useStore((state) => state.currUser());
  const { refetch: updateSummary } = useAccountSummaryQuery(() => {});
  const { refetch: toggleAutoSavings, isFetching } = useTaxBucketSetPercentAndActivate({
    customerId,
    savePercent: autoSavingOn ? DISABLED_SAVE_PERCENT_VALUE : savePercent || DEFAULT_SAVE_PERCENT_VALUE,
    onSuccess: () => {
      setAutoSavingOn(!autoSavingOn);
    },
  });

  const getAutoSavingAmount = () => {
    if (savePercent) {
      return parseFloat((savePercent * 100).toFixed(1));
    }
    if (summary?.taxBucketBalance?.savePercent) {
      return parseFloat((summary.taxBucketBalance.savePercent * 100).toFixed(1));
    }
    return 0;
  };

  useEffect(() => {
    if (summary?.taxBucketBalance?.savePercent && summary?.taxBucketBalance?.savePercent !== savePercent) {
      setSavePercent(summary.taxBucketBalance.savePercent);
    }
  }, [summary?.taxBucketBalance?.savePercent]);

  useEffect(() => {
    updateSummary();
  }, [autoSavingOn]);

  useEffect(() => {
    updateSummary();
  }, []);

  const { t } = useTranslation();

  return (
    <div className='bg-surface-primary-bw-00 h-[314px] flex flex-col rounded-[12px] shadow-widgetCard border border-outline-primary-bw-03'>
      <div className='flex w-full border-b-outline-primary-bw-03 border-b-[1px] px-[32px] pt-[24px] pb-[16px]'>
        <div className='flex justify-between items-center w-full'>
          <Text.H9 fontWeight={FontWeight.SEMIBOLD} id='autoSavingsTitle'>
            {t(HEADER)}
          </Text.H9>
          <LiliSwitch
            id='toggleAutoSavings'
            checked={autoSavingOn}
            onChange={() => {
              toggleAutoSavings();
            }}
          />
        </div>
      </div>
      <div className='w-full flex flex-col gap-[8px] items-center px-[32px] py-[24px]'>
        <div className='w-full flex flex-row gap-[8px] items-center'>
          <div className=' flex flex-col'>
            <div className='flex flex-row w-full text-action03'>
              {isFetching ? (
                <Skeleton shape='circle' size='72px' />
              ) : (
                <Donut
                  progressColor={autoSavingOn ? '' : 'stroke-[#A8B2C8]'}
                  circleColor={autoSavingOn ? '' : 'stroke-[#DFE2EB]'}
                  percentageClassName={`font-metricSemibold text-[18px] ${autoSavingOn ? 'text-action03' : 'text-bw05'}`}
                  percentage={getAutoSavingAmount()}
                  squareSize={72}
                  strokeWidth={6.65}
                />
              )}
            </div>
          </div>
          <div className='flex flex-col w-full'>
            {isFetching ? (
              <>
                <Skeleton className='mb-2' />
                <Skeleton width='10rem' className='mb-2' />
              </>
            ) : (
              <Text.H9
                id='autoSavingsText'
                className={autoSavingOn ? 'text-text-main-primary' : 'text-text-main-disabled'}
              >
                <span id='autoSavingsPercentage'>{getAutoSavingAmount()}</span>
                {t(MAIN_TEXT)}
              </Text.H9>
            )}
          </div>
        </div>
        {isFetching ? (
          <Skeleton className='mt-[8px]' height='44px' width='208px' />
        ) : (
          <LiliButton
            id='navigateModifyTaxSaving'
            disabled={!autoSavingOn}
            onClick={() => {
              navigate('/tax-bucket-personalization');
            }}
            className='w-[208px] mt-[8px] h-[44px]'
            title={t(BUTTON_TEXT)}
          />
        )}
        <span
          id='autoSavingsDisclaimer'
          className='text-xxs font-metric leading-[1rem] text-bw06 text-opacity-95 pt-[16px]'
        >
          {t(DISCLAIMER)}
        </span>
      </div>
    </div>
  );
};

export default TaxBucketAutoSaving;
